/**
 * Titration arithmetic: unknown concentrations, equivalence volumes and pH curves.
 *
 * ## Concentration at the endpoint
 *
 * For a·analyte + b·titrant → products, at the equivalence point
 * n(titrant) ÷ b = n(analyte) ÷ a, so
 *
 *     c(analyte) = c(titrant) × V(titrant) × a ÷ (b × V(analyte)).
 *
 * ## The curve
 *
 * A monoprotic acid or base is titrated with a strong base or acid of the opposite kind.
 * After adding V mL of titrant to V₀ mL of analyte the solution has volume V₀ + V, the
 * analyte is diluted to C₀V₀ ÷ (V₀ + V) and the titrant's spectator ion is at
 * Ct·V ÷ (V₀ + V). The charge balance in `acid-base.ts` then gives the exact pH — buffer
 * region, equivalence point and excess alike — with no case-by-case approximations.
 */

import {
  phOfMixture,
  type Solute,
} from "@/lib/tool-kit/chem-reactions/acid-base";
import {
  INDICATORS,
  type Indicator,
} from "@/lib/tool-kit/chem-reactions/acid-base-data";

export interface EndpointInput {
  /** mol/L */
  titrantConcentration: number;
  /** any volume unit, as long as it matches `analyteVolume` */
  titrantVolume: number;
  analyteVolume: number;
  /** Coefficient of the analyte in the balanced equation. */
  analyteCoefficient: number;
  /** Coefficient of the titrant in the balanced equation. */
  titrantCoefficient: number;
}

export interface EndpointResult {
  /** Moles of titrant, in mol per (volume unit ÷ litre): see `volumeToLitres`. */
  titrantMoles: number;
  analyteMoles: number;
  /** mol/L */
  analyteConcentration: number;
}

/** Volumes in mL → mol/L working: pass `volumeToLitres = 1e-3`. */
export function analyteFromEndpoint(
  input: EndpointInput,
  volumeToLitres: number
): EndpointResult {
  const titrantMoles =
    input.titrantConcentration * input.titrantVolume * volumeToLitres;
  const analyteMoles =
    (titrantMoles * input.analyteCoefficient) / input.titrantCoefficient;
  return {
    titrantMoles,
    analyteMoles,
    analyteConcentration: analyteMoles / (input.analyteVolume * volumeToLitres),
  };
}

/** Titrant volume (same unit as the analyte volume) needed to reach equivalence. */
export function equivalenceVolume(
  analyteConcentration: number,
  analyteVolume: number,
  titrantConcentration: number,
  analyteCoefficient: number,
  titrantCoefficient: number
): number {
  return (
    (analyteConcentration * analyteVolume * titrantCoefficient) /
    (analyteCoefficient * titrantConcentration)
  );
}

/* ----------------------------------------------------------------- curve */

export interface CurveInput {
  solute: Solute;
  /** mol/L */
  analyteConcentration: number;
  /** mL */
  analyteVolume: number;
  /** mol/L of the strong titrant */
  titrantConcentration: number;
}

export interface CurvePoint {
  /** mL of titrant added */
  volume: number;
  pH: number;
}

/** pH after adding `volume` mL of titrant. */
export function pHAt(input: CurveInput, volume: number): number {
  const total = input.analyteVolume + volume;
  return phOfMixture({
    solute: input.solute,
    concentration: (input.analyteConcentration * input.analyteVolume) / total,
    titrantIon: (input.titrantConcentration * volume) / total,
  });
}

export function curveEquivalence(input: CurveInput): number {
  return (
    (input.analyteConcentration * input.analyteVolume) /
    input.titrantConcentration
  );
}

const CURVE_STEPS = 160;
const CURVE_SPAN = 2;
const DENSE = [0.001, 0.005, 0.01, 0.02, 0.05];

/** Points from 0 to twice the equivalence volume, denser around the steep part. */
export function titrationCurve(input: CurveInput): CurvePoint[] {
  const equivalence = curveEquivalence(input);
  const volumes = new Set<number>();
  for (let i = 0; i <= CURVE_STEPS; i++) {
    volumes.add((equivalence * CURVE_SPAN * i) / CURVE_STEPS);
  }
  for (const fraction of DENSE) {
    volumes.add(equivalence * (1 - fraction));
    volumes.add(equivalence * (1 + fraction));
  }
  return [...volumes]
    .sort((a, b) => a - b)
    .map((volume) => ({ volume, pH: pHAt(input, volume) }));
}

export interface KeyPoint {
  id: "start" | "half" | "equivalence" | "beyond";
  volume: number;
  pH: number;
}

export function keyPoints(input: CurveInput): KeyPoint[] {
  const equivalence = curveEquivalence(input);
  const at = (id: KeyPoint["id"], volume: number): KeyPoint => ({
    id,
    volume,
    pH: pHAt(input, volume),
  });
  return [
    at("start", 0),
    at("half", equivalence / 2),
    at("equivalence", equivalence),
    at("beyond", equivalence * 1.5),
  ];
}

const JUMP_WINDOW = 0.005;

/** The pH interval the curve crosses within ±0.5 % of the equivalence volume. */
export function steepRange(input: CurveInput): { low: number; high: number } {
  const equivalence = curveEquivalence(input);
  const a = pHAt(input, equivalence * (1 - JUMP_WINDOW));
  const b = pHAt(input, equivalence * (1 + JUMP_WINDOW));
  return { low: Math.min(a, b), high: Math.max(a, b) };
}

export interface IndicatorFit {
  indicator: Indicator;
  suitable: boolean;
}

/**
 * An indicator suits a titration when the middle of its colour-change interval falls inside
 * the steep part of the curve, so one drop moves it across the whole change.
 */
export function indicatorFits(input: CurveInput): IndicatorFit[] {
  const { low, high } = steepRange(input);
  return INDICATORS.map((indicator) => {
    const middle = (indicator.low + indicator.high) / 2;
    return { indicator, suitable: middle >= low && middle <= high };
  });
}

/** A weak acid or base has a buffer region, and at half-equivalence pH = pKa (of the acid). */
export function isWeak(solute: Solute): boolean {
  return solute.kind === "weak-acid" || solute.kind === "weak-base";
}
